<template>
  <div class="crypto-page">
    <div class="crypto-shell">
      <header class="crypto-head">
        <button type="button" class="head-btn" :aria-label="$t('auth.back')" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>
        <div class="head-copy">
          <h1>{{ copy.title }}</h1>
          <p>{{ copy.subtitle }}</p>
        </div>
        <button type="button" class="head-btn" :aria-label="$t('auth.home')" @click="goHome">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 11l9-8 9 8"></path>
            <path d="M5 10v10h14V10"></path>
          </svg>
        </button>
      </header>

      <main class="crypto-content">
        <div v-if="isBound" class="status-banner">{{ copy.boundTip }}</div>

        <section class="form-card">
          <div class="form-field">
            <label>{{ copy.chainLabel }}</label>
            <div class="chain-options">
              <button
                v-for="option in chainOptions"
                :key="option"
                type="button"
                class="chain-option"
                :class="{ on: form.chain_type === option }"
                @click="form.chain_type = option"
              >
                {{ option }}
              </button>
            </div>
          </div>

          <div class="form-field">
            <label>{{ copy.addressLabel }}</label>
            <input
              v-model.trim="form.address"
              class="form-input"
              type="text"
              autocomplete="off"
              :placeholder="copy.addressPlaceholder"
            />
          </div>

          <button type="button" class="submit-btn" :disabled="submitting" @click="submitBind">
            {{ submitting ? copy.submittingText : copy.submitText }}
          </button>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";

const CHAIN_OPTIONS = ["USDT-TRC20", "USDT-ERC20"];

export default {
  data() {
    return {
      submitting: false,
      isBound: false,
      chainOptions: CHAIN_OPTIONS,
      form: {
        chain_type: "USDT-TRC20",
        address: "",
      },
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "cryptoBind");
    },
  },
  created() {
    if (!localStorage.getItem("token")) {
      this.$toast(this.copy.loginFirst);
      this.$router.replace("/Login");
      return;
    }
    this.loadExisting();
  },
  mounted() {
    document.title = `${this.copy.title} · OMN`;
  },
  methods: {
    normalizeChainType(value) {
      const raw = String(value || "").trim().toUpperCase();
      if (raw.indexOf("ERC") !== -1) {
        return "USDT-ERC20";
      }
      return "USDT-TRC20";
    },
    async loadExisting() {
      try {
        const res = await this.$http({
          method: "get",
          url: "user_get_bank",
        });
        if (!(res && res.code === 200 && res.data)) {
          return;
        }
        const crypto = res.data.crypto || {};
        this.isBound = !!crypto.is_bind;
        const info = crypto.info || {};
        if (info.chain_type) {
          this.form.chain_type = this.normalizeChainType(info.chain_type);
        }
        if (info.address) {
          this.form.address = info.address;
        }
      } catch (error) {
        // ignore
      }
    },
    async submitBind() {
      if (!this.form.chain_type || this.chainOptions.indexOf(this.form.chain_type) === -1) {
        this.$toast(this.copy.chainError);
        return;
      }
      if (!this.form.address) {
        this.$toast(this.copy.addressError);
        return;
      }
      this.submitting = true;
      try {
        const res = await this.$http({
          method: "post",
          url: "user_set_crypto",
          data: {
            chain_type: this.form.chain_type,
            address: this.form.address,
          },
        });
        if (res && res.code === 200) {
          this.$toast(res.msg || this.copy.submitText);
          this.isBound = true;
          return;
        }
        this.$toast((res && res.msg) || this.copy.submitText);
      } catch (error) {
        this.$toast(this.copy.submitText);
      } finally {
        this.submitting = false;
      }
    },
    back() {
      window.history.back();
    },
    goHome() {
      this.$router.push("/Home");
    },
  },
};
</script>

<style lang="less" scoped>
.crypto-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 63, 120, 0.11), transparent 28%),
    #0a0710;
  color: #fff7fb;
}

.crypto-shell {
  width: 100%;
  max-width: 25rem;
  margin: 0 auto;
}

.crypto-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 2.625rem 1fr 2.625rem;
  align-items: center;
  min-height: 4.1875rem;
  padding: max(0.5rem, env(safe-area-inset-top)) 0.8125rem 0.5rem;
  background: rgba(10, 7, 16, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
}

.head-btn {
  width: 2.375rem;
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
  cursor: pointer;
}

.head-btn svg {
  width: 1.1875rem;
  height: 1.1875rem;
}

.head-copy h1 {
  margin: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 900;
}

.head-copy p {
  margin: 0.1875rem 0 0;
  text-align: center;
  color: #a89baa;
  font-size: 0.5625rem;
}

.crypto-content {
  padding: 1rem;
}

.status-banner {
  margin-bottom: 0.875rem;
  padding: 0.75rem 0.9375rem;
  border: 1px solid rgba(77, 227, 165, 0.32);
  border-radius: 0.9375rem;
  color: #67e7b3;
  font-size: 0.75rem;
  font-weight: 800;
}

.form-card {
  padding: 1rem;
  border-radius: 1.375rem;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.085);
}

.form-field + .form-field {
  margin-top: 0.875rem;
}

.form-field label {
  display: block;
  margin-bottom: 0.4375rem;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 800;
}

.form-input {
  width: 100%;
  height: 3.125rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  padding: 0 0.875rem;
  outline: none;
}

.form-input::placeholder {
  color: #776b76;
}

.chain-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
}

.chain-option {
  height: 3.125rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  background: rgba(255, 255, 255, 0.05);
  color: #bfb2bd;
  font-size: 0.8125rem;
  font-weight: 800;
  cursor: pointer;
}

.chain-option.on {
  color: #ff91b1;
  border-color: rgba(255, 77, 133, 0.55);
  background: rgba(255, 63, 120, 0.14);
}

.submit-btn {
  width: 100%;
  height: 3.125rem;
  margin-top: 1.5rem;
  border: 0;
  border-radius: 1.5625rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  color: #fff;
  font-size: 1.0625rem;
  font-weight: 900;
  cursor: pointer;
}

.submit-btn[disabled] {
  opacity: 0.6;
}
</style>
